/**
 * WebRTC Provider
 * Facade Pattern: Encapsulates all WebRTC logic and provides clean API to UI components
 * WebRTCPlayer should ONLY access API layer through this provider
 */

import { WebRTCConnection } from './WebRTCConnection';
import { GenericSDKLoader, createWebRTCSDKLoader } from './SDKLoader';
import { WebRTCFactory } from './WebRTCFactory';
import type { LiveCameraChannel } from '../live/types';
import { 
  startLiveStreamingWorkflow, 
  stopLiveView,
  changeLiveDirectionWithRetry,
  startTwoWayAudio,
  stopTwoWayAudio
} from '../live/api';
import type { TwoWayStatus } from '../live/types';

/**
 * Media device capabilities interface
 */
export interface MediaDeviceCapabilities {
  microphone: {
    supported: boolean;
    available: boolean;
    hasPermission: boolean | null;
    deviceCount: number;
    error?: string;
  };
  speaker: {
    supported: boolean;
    available: boolean;
    deviceCount: number;
    error?: string;
  };
  getUserMedia: {
    supported: boolean;
    error?: string;
  };
}

/**
 * Check browser media device support and availability
 * 브라우저의 마이크/스피커 지원 및 가용성 확인
 * @returns Promise with detailed media capabilities
 */
export async function checkMediaDeviceSupport(): Promise<MediaDeviceCapabilities> {
  const capabilities: MediaDeviceCapabilities = {
    microphone: {
      supported: false,
      available: false,
      hasPermission: null,
      deviceCount: 0
    },
    speaker: {
      supported: false,
      available: false,
      deviceCount: 0
    },
    getUserMedia: {
      supported: false
    }
  };

  try {
    // 1. Check getUserMedia support
    capabilities.getUserMedia.supported = !!(
      navigator.mediaDevices && 
      navigator.mediaDevices.getUserMedia
    );

    if (!capabilities.getUserMedia.supported) {
      capabilities.getUserMedia.error = 'getUserMedia not supported in this browser';
      return capabilities;
    }

    // 2. Check enumerateDevices support
    if (!navigator.mediaDevices.enumerateDevices) {
      capabilities.microphone.error = 'enumerateDevices not supported';
      capabilities.speaker.error = 'enumerateDevices not supported';
      return capabilities;
    }

    // 3. Enumerate available devices
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      // Count microphones
      const microphones = devices.filter(device => device.kind === 'audioinput');
      capabilities.microphone.supported = microphones.length > 0;
      capabilities.microphone.available = microphones.some(mic => mic.deviceId !== '');
      capabilities.microphone.deviceCount = microphones.length;
      
      // Count speakers
      const speakers = devices.filter(device => device.kind === 'audiooutput');
      capabilities.speaker.supported = speakers.length > 0;
      capabilities.speaker.available = speakers.some(speaker => speaker.deviceId !== '');
      capabilities.speaker.deviceCount = speakers.length;
      
    } catch (error) {
      capabilities.microphone.error = `Device enumeration failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      capabilities.speaker.error = capabilities.microphone.error;
    }

    // 4. Test microphone access (to check permissions)
    if (capabilities.microphone.supported) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: true, 
          video: false 
        });
        
        // Microphone access successful
        capabilities.microphone.hasPermission = true;
        
        // Stop the stream immediately
        stream.getTracks().forEach(track => track.stop());
        
      } catch (error) {
        // Check specific permission errors
        if (error instanceof Error) {
          if (error.name === 'NotAllowedError') {
            capabilities.microphone.hasPermission = false;
            capabilities.microphone.error = 'Microphone permission denied';
          } else if (error.name === 'NotFoundError') {
            capabilities.microphone.available = false;
            capabilities.microphone.error = 'No microphone device found';
          } else if (error.name === 'NotReadableError') {
            capabilities.microphone.available = false;
            capabilities.microphone.error = 'Microphone is being used by another application';
          } else {
            capabilities.microphone.error = `Microphone access failed: ${error.message}`;
          }
        }
      }
    }

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    capabilities.microphone.error = errorMessage;
    capabilities.speaker.error = errorMessage;
    capabilities.getUserMedia.error = errorMessage;
  }

  return capabilities;
}

/**
 * Request microphone permission
 * 마이크 권한 요청
 * @returns Promise with permission result
 */
export async function requestMicrophonePermission(): Promise<{
  granted: boolean;
  stream?: MediaStream;
  error?: string;
}> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ 
      audio: true, 
      video: false 
    });
    
    return {
      granted: true,
      stream
    };
    
  } catch (error) {
    let errorMessage = 'Microphone permission denied';
    
    if (error instanceof Error) {
      switch (error.name) {
        case 'NotAllowedError':
          errorMessage = 'User denied microphone permission';
          break;
        case 'NotFoundError':
          errorMessage = 'No microphone device found';
          break;
        case 'NotReadableError':
          errorMessage = 'Microphone is being used by another application';
          break;
        default:
          errorMessage = error.message;
      }
    }
    
    return {
      granted: false,
      error: errorMessage
    };
  }
}

/**
 * Connection state types for UI feedback
 */
export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'failed';

/**
 * Connection statistics for performance monitoring
 */
export interface ConnectionStats {
  bytesReceived: number;
  packetsReceived: number;
  packetsLost: number;
  jitter: number;
  roundTripTime: number;
}

/**
 * ICE server configuration for WebRTC
 */
export interface ICEServer {
  urls: string[];
  username?: string;
  credential?: string;
}

/**
 * WebRTC endpoints by protocol
 */
export interface EndpointsByProtocol {
  HTTPS: string;
  WSS: string;
}

/**
 * WebRTC Provider configuration interface
 */
export interface WebRTCProviderConfig {
  deviceId: string;
  channel: string;
  clientId?: string;
  role?: 'VIEWER' | 'MASTER';
  enableStats?: boolean;
  statsInterval?: number;
}

/**
 * WebRTC Provider event callbacks interface
 * UI components register these to receive real-time updates
 */
export interface WebRTCProviderCallbacks {
  onStateChange?: (state: ConnectionState) => void;
  onError?: (error: Error) => void;
  onStreamReceived?: (stream: MediaStream) => void;
  onStatsUpdate?: (stats: ConnectionStats) => void;
}

/**
 * WebRTC Connection configuration interface
 */
export interface WebRTCConnectionConfig {
  deviceId: string;
  channel: string;
  clientId?: string;
  role?: 'VIEWER' | 'MASTER';
  enableStats?: boolean;
  statsInterval?: number;
  provider: WebRTCProviderInterface;
  webrtcConfig?: InternalWebRTCConfig; // Pre-obtained config to prevent duplicate API calls
  
  // Event callbacks
  onStateChange?: (state: ConnectionState) => void;
  onError?: (error: Error) => void;
  onStreamReceived?: (stream: MediaStream) => void;
  onStatsUpdate?: (stats: ConnectionStats) => void;
}

/**
 * WebRTC Connection events interface
 */
export interface WebRTCConnectionEvents {
  onStateChange: (state: ConnectionState) => void;
  onError: (error: Error) => void;
  onStreamReceived: (stream: MediaStream) => void;
  onStatsUpdate: (stats: ConnectionStats) => void;
}

/**
 * Internal WebRTC configuration (mapped from FleetA API)
 */
export interface InternalWebRTCConfig {
  region: string;
  channelARN: string;
  url: string;
  iceServers: ICEServer[];
  endpointsByProtocol: EndpointsByProtocol;
  role: 'VIEWER' | 'MASTER';
  clientId: string;
}

/**
 * WebRTC Provider interface for connection
 */
export interface WebRTCProviderInterface {
  getWebRTCConfig(config: any): Promise<InternalWebRTCConfig>;
  stopStreaming(deviceId: string): Promise<void>;
  changeChannel(deviceId: string, channel: string): Promise<void>;
}

/**
 * WebRTC Provider Class
 * 🎯 Facade Pattern: Single entry point for all WebRTC functionality
 * 
 * Main provider for WebRTC live streaming functionality.
 * Handles SDK loading, connection management, and streaming lifecycle.
 */
export class WebRTCProvider {
  // Provider configuration
  private config: WebRTCProviderConfig;
  
  // Internal connection management (hidden from UI)
  private connection: WebRTCConnection | null = null;
  private sdkLoader: GenericSDKLoader;
  private factory: WebRTCFactory;
  
  // State management
  private currentState: ConnectionState = 'idle';
  private currentError: Error | null = null;
  private currentStats: ConnectionStats | null = null;
  
  // Event deduplication
  // Callback registry for UI components
  private callbacks: WebRTCProviderCallbacks = {};
  
  /**
   * Create WebRTC Provider instance
   * @param config - Provider configuration
   */
  constructor(config: WebRTCProviderConfig) {
    this.config = config;
    this.sdkLoader = createWebRTCSDKLoader();
    this.factory = new WebRTCFactory();
  }

  /**
   * Register event callbacks for UI updates
   * @param callbacks - Event callback functions
   */
  setCallbacks(callbacks: WebRTCProviderCallbacks): void {
    
    this.callbacks = callbacks;
  }
  
  /**
   * Connect to live stream
   * Main API method - encapsulates entire connection workflow
   */
  async connect(): Promise<void> {
    // Prevent duplicate connections
    if (this.currentState === 'connecting' || this.currentState === 'connected') {
      return;
    }

    try {
      this.setState('connecting');
      this.setError(null);

      // Step 1: Ensure WebRTC SDK is loaded
      await this.ensureSDKLoaded();

      // Step 2: Get WebRTC configuration from FleetA API
      const webrtcConfig = await this.getWebRTCConfigFromAPI();

      // Step 3: Create WebRTC connection with pre-loaded SDK
      this.connection = this.factory.createConnection({
        deviceId: this.config.deviceId,
        channel: this.config.channel,
        clientId: this.config.clientId || this.config.deviceId,
        role: this.config.role || 'VIEWER',
        enableStats: this.config.enableStats ?? true,
        statsInterval: this.config.statsInterval || 2000,
        provider: this,
        webrtcConfig: webrtcConfig, // Pass pre-obtained config to prevent duplicate API calls
        
        // Internal callback handlers (UI callbacks are called from here)
        onStateChange: (state) => this.setState(state),
        onError: (error) => this.setError(error),
        onStreamReceived: (stream) => this.handleStreamReceived(stream),
        onStatsUpdate: (stats) => this.handleStatsUpdate(stats)
      });

      // Step 4: Start WebRTC connection
      await this.connection.connect();

    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Connection failed');
      this.setError(errorObj);
      this.setState('failed');
      throw errorObj;
    }
  }
  
  /**
   * Disconnect from live stream
   * Main API method - handles cleanup and API calls
   */
  async disconnect(): Promise<void> {
    try {
      // Step 1: Close WebRTC connection
      if (this.connection) {
        await this.connection.disconnect();
        this.connection = null;
      }

      // Step 2: Stop streaming via FleetA API
      try {
        await stopLiveView({ psn: this.config.deviceId });
      } catch (error) {
        // Ignore API errors during disconnect
      }

      // Step 3: Reset state
      this.currentStats = null;
      this.setState('disconnected');

    } catch (error) {
      // Always set disconnected state even on error
      this.setState('disconnected');
    }
  }
  
  /**
   * Change camera channel
   * Main API method - handles FleetA API call
   * @param channel - New channel to switch to
   */
  async changeChannel(channel: string): Promise<void> {
    if (channel === this.config.channel || this.currentState !== 'connected') {
      return;
    }

    try {
      // Call FleetA API to change channel
      const channelMapping: Record<string, LiveCameraChannel> = {
        'front': '1',
        'rear': '2',
        'interior': '3',
        'exterior': '4'
      };
      
      const mappedChannel = channelMapping[channel] || '1';
      
      // Use FleetA API with retry logic
      await changeLiveDirectionWithRetry({ 
        psn: this.config.deviceId,
        param1: mappedChannel
      });
      
      // Update configuration
      this.config.channel = channel;
      
    } catch (error) {
      throw new Error(`Failed to change channel: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Get current connection state
   * API method for state queries
   */
  getState(): ConnectionState {
    return this.currentState;
  }
  
  /**
   * Get current connection statistics
   * API method for statistics queries
   */
  getStats(): ConnectionStats | null {
    return this.currentStats;
  }
  
  /**
   * Get current error
   * API method for error queries
   */
  getError(): Error | null {
    return this.currentError;
  }
  
  /**
   * Get current local audio stream
   * API method to access local audio stream for monitoring
   */
  getLocalAudioStream(): MediaStream | null {
    if (!this.connection) {
      return null;
    }
    
    return this.connection.getLocalAudioStream();
  }
  
  /**
   * Update provider configuration
   * API method for configuration updates
   * @param newConfig - New configuration values
   */
  updateConfig(newConfig: Partial<WebRTCProviderConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
  
  /**
   * Clean up all resources
   * API method for cleanup
   */
  destroy(): void {
    
    if (this.connection) {
      this.connection.destroy();
      this.connection = null;
    }
    
    // Reset deduplication state
    this.callbacks = {};
    this.currentStats = null;
    this.setState('idle');
  }
  
  /**
   * Update factory dependencies (useful for testing)
   */
  setFactoryDependencies(dependencies: Partial<import('./WebRTCFactory').WebRTCDependencies>): void {
    this.factory.updateDependencies(dependencies);
  }
  
  /**
   * Start two-way audio communication
   * API method to start bidirectional audio
   * @param deviceId - Device ID to start two-way audio
   * @returns Promise with two-way audio status
   */
  async startTwoWayAudio(deviceId: string): Promise<TwoWayStatus> {
    try {
      // Step 1: Start FleetA API two-way audio
      const response = await startTwoWayAudio({
        psn: deviceId,
        tokenType: 'web'
      });
      
      if (response.status === 'busy') {
        throw new Error('Two-way audio is busy. Another user is currently using audio communication.');
      }
      
      // Step 2: Start WebRTC audio on the connection
      if (this.connection) {
        try {
          // Start audio process but don't report success yet
          await this.connection.startTwoWayAudio();
        } catch (error) {
          // If WebRTC audio fails, stop the FleetA API session
          try {
            await stopTwoWayAudio({ psn: deviceId, tokenType: 'web' });
          } catch (stopError) {
          }
          throw error;
        }
      } else {
        throw new Error('No WebRTC connection available');
      }
      
      return response.status || 'free';
      
    } catch (error) {
      const errorMessage = `Failed to start two-way audio: ${error instanceof Error ? error.message : 'Unknown error'}`;
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Stop two-way audio communication
   * API method to stop bidirectional audio
   * @param deviceId - Device ID to stop two-way audio
   * @returns Promise with two-way audio status
   */
  async stopTwoWayAudio(deviceId: string): Promise<TwoWayStatus> {
    // Add stack trace to see who called stop
    try {
      // Step 1: Stop WebRTC audio on the connection
      if (this.connection) {
        try {
          await this.connection.stopTwoWayAudio();
        } catch (error) {
          // Continue with FleetA API cleanup even if WebRTC fails
        }
      } else {
      }
      
      // Step 2: Stop FleetA API two-way audio
      const response = await stopTwoWayAudio({
        psn: deviceId,
        tokenType: 'web'
      });
      
      return response.status || 'free';
      
    } catch (error) {
      const errorMessage = `Failed to stop two-way audio: ${error instanceof Error ? error.message : 'Unknown error'}`;
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Set local audio enabled/disabled (microphone mute/unmute)
   * API method to control microphone
   * @param enabled - Whether local audio should be enabled
   */
  setLocalAudioEnabled(enabled: boolean): void {
    if (!this.connection) {
      return;
    }
    
    this.connection.setLocalAudioEnabled(enabled);
  }
  
  // ========================
  // INTERNAL METHODS (hidden from UI)
  // ========================
  
  /**
   * Ensure WebRTC SDK is loaded
   */
  private async ensureSDKLoaded(): Promise<void> {
    if (this.sdkLoader.isLoaded()) {
      // Update factory dependencies with loaded SDK
      this.updateFactoryWithLoadedSDK();
      return;
    }
    
    if (this.sdkLoader.isLoading()) {
      // Wait for ongoing load
      await this.sdkLoader.loadSDK();
      this.updateFactoryWithLoadedSDK();
      return;
    }
    
    // Start loading
    await this.sdkLoader.loadSDK();
    
    if (this.sdkLoader.getError()) {
      throw new Error(`Failed to load WebRTC SDK: ${this.sdkLoader.getError()}`);
    }
    
    // Update factory dependencies after successful load
    this.updateFactoryWithLoadedSDK();
  }
  
  /**
   * Update factory with loaded WebRTC SDK
   */
  private updateFactoryWithLoadedSDK(): void {
    // Get the loaded SDK from window object
    const webrtcSDK = typeof window !== 'undefined' ? (window as any).KVSWebRTC : null;
    
    if (webrtcSDK) {
      this.factory.updateDependencies({
        webrtcSDK: webrtcSDK
      });
    } else {
      throw new Error('WebRTC SDK not available on window object after loading');
    }
  }
  
  /**
   * Get WebRTC configuration from FleetA API
   */
  private async getWebRTCConfigFromAPI(): Promise<InternalWebRTCConfig> {
    try {
      // Map channel string to LiveCameraChannel type
      const channelMapping: Record<string, LiveCameraChannel> = {
        'front': '1',
        'rear': '2',
        'interior': '3',
        'exterior': '4'
      };
      
      const mappedChannel = channelMapping[this.config.channel] || '1';
      
      // Call FleetA API workflow
      const webrtcConfig = await startLiveStreamingWorkflow(
        this.config.deviceId, 
        mappedChannel, 
        this.config.clientId || this.config.deviceId, 
        this.config.role || 'VIEWER'
      );
      
      // Convert to internal config format
      return {
        region: webrtcConfig.region,
        channelARN: webrtcConfig.channelARN,
        url: webrtcConfig.url,
        iceServers: webrtcConfig.iceServers.map(server => ({
          urls: server.urls,
          username: server.username,
          credential: server.credential
        })),
        endpointsByProtocol: webrtcConfig.endpointsByProtocol,
        role: webrtcConfig.role,
        clientId: webrtcConfig.clientId
      };
    } catch (error) {
      throw new Error(`Failed to get WebRTC config: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Set connection state and notify UI
   */
  private setState(state: ConnectionState): void {
    if (this.currentState !== state) {
      this.currentState = state;
      this.callbacks.onStateChange?.(state);
    }
  }
  
  /**
   * Set error and notify UI
   */
  private setError(error: Error | null): void {
    this.currentError = error;
    if (error) {
      this.callbacks.onError?.(error);
    }
  }
  
  /**
   * Handle stream received from WebRTC connection
   */
  private handleStreamReceived(stream: MediaStream): void {
    // WebRTCConnection already handles deduplication, so just forward the stream
    this.callbacks.onStreamReceived?.(stream);
  }
  
  /**
   * Handle statistics update from WebRTC connection
   */
  private handleStatsUpdate(stats: ConnectionStats): void {
    this.currentStats = stats;
    this.callbacks.onStatsUpdate?.(stats);
  }
  
  // ========================
  // LEGACY METHODS (for WebRTCConnection compatibility)
  // ========================
  
  /**
   * Get WebRTC configuration (for WebRTCConnection internal use)
   * @internal
   */
  async getWebRTCConfig(config: {
    deviceId: string;
    channel: string;
    clientId?: string;
    role?: 'VIEWER' | 'MASTER';
  }): Promise<InternalWebRTCConfig> {
    throw new Error('getWebRTCConfig() should not be called when pre-obtained config is available');
  }
  
  /**
   * Stop streaming (for WebRTCConnection internal use)
   * @internal
   */
  async stopStreaming(deviceId: string): Promise<void> {
    try {
      await stopLiveView({ psn: deviceId });
    } catch (error) {
      throw new Error(`Failed to stop streaming: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
/**
 * Create WebRTC Provider instance
 * 🎯 Factory function for UI components
 * @param config - Provider configuration
 * @returns WebRTC Provider instance
 */
export function createWebRTCProvider(config: WebRTCProviderConfig): WebRTCProvider {
  return new WebRTCProvider(config);
}